// LeftMenu.vue
<template>
  <div id="left-menu" :class="{ opened: menu_expanded }">
    <!-- Header -->
    <LeftMenuHeader
      :user="user"
      :showOptions="showOptions"
      @click="redirect({routeName: 'club'})"
    />
    <!-- Body -->
    <div class="mbody hide-scrollbar" ref="sidebar">
      <AdminMenu
        v-if="user.admin && event"
        :user="user"
        :club="club"
        :categories="categories"
        :event="event"
        :activePage="activePage"
        :selectedTeam="selectedTeam"
        :integra="integra"
        :showOptions="showOptions"
        @changeTeam="changeTeam"
        @collapse="collapseCategory"
        @redirect="redirect"
        @logout="logout"
        @openRules="openRules"
        @openGroups="openGroups"
        @openCompetitionSystem="openCompetitionSystem"
        @openCalendar="openCalendar"
        @openProtocolChildren="openProtocolChildren"
      />

      <UserMenu
        v-else-if="event && typeof event === 'object'"
        :user="user"
        :club="team.club"
        :categories="categories"
        :event="event"
        :activePage="activePage"
        :selectedTeam="selectedTeam"
        :integra="integra"
        :showOptions="showOptions"
        @redirect="redirect"
        @collapse="collapseCategory"
        @openRules="openRules"
        @openCompetitionSystem="openCompetitionSystem"
        @openGroups="openGroups"
        @openCalendar="openCalendar"
        @changeTeam="changeTeam"
        @openProtocolChildren="openProtocolChildren"
      />

    <!-- Footer -->
      <SidebarFooter
        v-if="event && typeof event === 'object'"
        :event="event"
        :changeClub="canChangeClub"
        @logout="logout"
        @changeClub="changeClub"
      />

      <a href="http://micsports.es/" target="_blank">
        <div class="item" id="legend">
          <span class="description">{{ $t("organitzation") }}</span>
        </div>
      </a>
    </div>
  </div>
</template>

<script>
// Component Hierarchy Overview
// LeftMenu.vue
// ├── LeftMenuHeader.vue
// ├── AdminMenu.vue         ← if user.admin === true
// │   ├── MenuItem.vue
// │   ├── TeamMenuGroup.vue
// │   │   └── MenuItem.vue
// ├── UserMenu.vue          ← if user.admin === false
// │   ├── MenuItem.vue
// │   ├── TeamMenuGroup.vue
// │   │   └── MenuItem.vue
// ├── SidebarFooter.vue     ← always rendered inside .mbody
// │   └── Language.vue
/* global moment */
import { Api } from '@/core/api.js'
import SidebarFooter from '@/components/domain/home/left-menu/SidebarFooter.vue'
import LeftMenuHeader from '@/components/domain/home/left-menu/LeftMenuHeader.vue'
import AdminMenu from '@/components/domain/home/left-menu/AdminMenu.vue'
import UserMenu from '@/components/domain/home/left-menu/UserMenu.vue'
import { resolveRedirectRoute } from '@/helpers/left-menu-navigation.js'
import {
  openCovidDoc,
  openSimpleDoc,
  openCategoryDoc,
  openMyTeams,
  openStaticExternalDoc
} from '@/helpers/document-opener'

const api = new Api()

export default {
  name: 'LeftMenu',
  components: {
    SidebarFooter,
    LeftMenuHeader,
    AdminMenu,
    UserMenu
  },
  props: {
    expanded: { type: Boolean, default: false }
  },
  data () {
    return {
      activePage: ''
    }
  },
  computed: {
    menu_expanded () {
      return !!this.expanded
    },
    selectedTeam () {
      const id = this.$store.getters['getSelectedTeam']
      if (id) {
        return String(id)
      }
      const routeId = this.$route.params.id || this.$route.params.teamId || this.$route.params.team_id
      const result = routeId ? String(routeId) : ''
      return result
    },
    user () {
      return this.$store.getters['user/getUser']
    },
    club () {
      return this.$store.getters['club/getClub']
    },
    team () {
      return this.$store.getters['team/getTeam']
    },
    event () {
      return this.$store.getters.getCurrentEvent()
    },
    showOptions () {
      return this.$route.name !== 'sign-document'
    },
    canChangeClub () {
      return this.user && this.user.sel_club
    },
    categories () {
      const club = this.club || (this.team && this.team.club ? this.team.club : false)
      if (club) {
        let teams = Array.isArray(club.teams)
          ? club.teams
          : (club.teams && typeof club.teams === 'object'
            ? Object.values(club.teams)
            : false)

        if (!teams && this.team && this.team.team) {
          let team = { ...this.team.team }
          team.acompanyants = this.team.acompanyants
          team.acompanyants_housed = this.team.acompanyants_housed
          team.players = this.team.players
          team.players_housed = this.team.players_housed
          team.staff = this.team.staff
          team.staff_housed = this.team.staff_housed
          teams = [team]
        }

        if (teams) {
          return teams.reduce((groups, team) => {
            const { id, category } = team
            const catId = category.id
            if (!groups[catId]) groups[catId] = {}
            groups[catId][id] = team
            return groups
          }, {})
        }
      }
      return {}
    },
    integra () {
      return Object.values(this.categories)
        .flatMap((group) => Object.values(group))
        .some((team) => team.category.name === 'INTEGRA')
    }
  },
  methods: {
    onChangeRoute () {
      // Avoid resetting if we're already navigating
      if (this.$route.name !== this.activePage) {
        this.activePage = this.$route.name
      }
    },
    redirect ({ routeName, teamId }) {
      // console.log('Redirecting to:', routeName, teamId)
      // save scroll
      const sidebar = this.$refs.sidebar
      if (sidebar) {
        sessionStorage.setItem(
          'left-menu-scroll',
          String(sidebar.scrollTop)
        )
      }

      this.$overlay.hide()
      this.$emit('closeMenu')
      const route = resolveRedirectRoute(routeName, teamId, this.event)
      if (!route) return

      // navigate…
      this.$router
        .push(route)
        .then(() => {
          // then restore
          this.$nextTick(() => {
            const saved = parseInt(
              sessionStorage.getItem('left-menu-scroll') || '',
              10
            )
            if (!isNaN(saved) && this.$refs.sidebar) {
              this.$refs.sidebar.scrollTop = saved
            }
          })
        })
        .catch(() => {})
    },
    collapseCategory (teamKey) {
      this.$store.commit('setSelectedTeam', teamKey)
    },
    changeTeam (teamId) {
      this.$store.commit('user/setTeamID', teamId)
    },
    logout () {
      this.$overlay.hide()
      this.$emit('closeMenu')
      localStorage.removeItem('team_id')

      api.get('logout').then(() => {
        const event = this.event
        this.$store.commit('user/setAuthenticate', false)
        this.$store.commit('setOverlayVisible', false)
        this.$store.commit('setUserMenuVisible', false)
        this.$router.replace({
          name: 'event_login',
          params: {
            event: this.$root.formatEventUrl(event.name, event.edition)
          }
        })
      })
    },
    async changeClub () {
      const event = this.event

      try {
        const response = await api.login({
          username: this.user.username,
          password: this.user.password,
          event: event.id,
          route: '/login'
        })

        this.$overlay.hide()

        if (response.status) {
          this.handleLoginResponse(response.data, event)
        } else {
          console.error('Login failed')
        }
      } catch (error) {
        console.error('Error during login:', error)
      }
    },
    handleLoginResponse (data, event) {
      if (data.sel_club) {
        this.$store.commit('user/setAuthenticateSelClub', data)
        this.redirectTo('sel_club', event)
      } else {
        this.$store.commit('user/setAuthenticate', data)
        localStorage.setItem('token', data.token)

        this.$store.dispatch('loadTablesRelationship').then(() => {
          if (this.campus) {
            this.$store.commit('setCampus', true)
          }
          this.redirectTo('home', event)
        })
      }
    },
    redirectTo (routeName, event) {
      this.$router.push({
        name: routeName,
        params: {
          event: this.$root.formatEventUrl(event.name, event.edition)
        }
      })
    },
    openRules () {
      const timestamp = moment().valueOf()
      const lang = this.user.language
      const event = this.event
      const base = process.env.VUE_APP_API_URL + '../static/document/'

      let suffix = this.getLangSuffix(lang)
      switch (+event.type_id) {
        case 1:
          return window.open(
            this.integra
              ? `${base}micfootball/MICIntegra-26_REGLAMENT.pdf?${timestamp}`
              : `${base}micfootball/REGLAMENTO-MICFootball${event.edition.substr(2)}_${suffix}.pdf?${timestamp}`
          )
        case 2:
          return window.open(
            `${base}micfootball7/REGLAMENTO-MICFootball7${event.edition.substr(2)}_${suffix}.pdf?${timestamp}`
          )
        case 14:
          return window.open(
            `${base}micfootball/REGLAMENTO-MICFootfem${event.edition.substr(2)}_${suffix}.pdf?${timestamp}`
          )
        case 15:
          return window.open(
            `${base}micfootballpuntacana/REGLAMENTO-MICFOOTBALLPUNTACANA'${event.edition.substr(2)}_${suffix}.pdf?${timestamp}`
          )
        case 17:
          return window.open(
            `${base}ligafuturamoeve/REGLAMENTO-${event.edition.substr(2)}.pdf?${timestamp}`
          )
        default:
      }
    },
    getLangSuffix (lang) {
      switch (lang) {
        case 'ca':
          return 'CAT'
        case 'es':
          return 'ES'
        case 'en':
          return 'EN'
        default:
          return 'ES'
      }
    },
    openGroups () {
      if (parseInt(this.event.type_id) === 1) {
        openSimpleDoc('MICFootball2025-Grups.pdf')
      } else if (parseInt(this.event.type_id) === 2) {
        openSimpleDoc('MICFootball72026-Grups.pdf')
      } else if (parseInt(this.event.type_id) === 14) {
        openSimpleDoc('MICFootfem-Grups.pdf')
      } else if (parseInt(this.event.type_id) === 15) {
        openSimpleDoc('MICFootballPuntacana2026-Grups.pdf')
      }
    },
    openCalendar () {
      if (parseInt(this.event.type_id) === 1) {
        openSimpleDoc('MICFootball2026-Calendari.pdf')
      } else if (parseInt(this.event.type_id) === 2) {
        openSimpleDoc('MICFootball72026-Calendari.pdf')
      } else if (parseInt(this.event.type_id) === 14) {
        openSimpleDoc('MICFootfem2026-Calendari.pdf')
      } else if (parseInt(this.event.type_id) === 15) {
        openSimpleDoc('MICFootballPuntacana2026-Calendari.pdf')
      }
    },
    openCompetitionSystem () {
      if (parseInt(this.event.type_id) === 1) {
        openSimpleDoc('MICFootball25-CompetitionSystem.pdf')
      } else if (parseInt(this.event.type_id) === 2) {
        openSimpleDoc('MICFootball72026-CompetitionSystem.pdf')
      } else if (parseInt(this.event.type_id) === 14) {
        openSimpleDoc('MICFootfem2026-CompetitionSystem.pdf')
      } else if (parseInt(this.event.type_id) === 15) {
        openSimpleDoc('MICFootballPuntacana2026-CompetitionSystem.pdf')
      }
    },
    openProtocolChildren () {
      if (parseInt(this.event.type_id) === 1) {
        openSimpleDoc('protocolos/protocolo-proteccion-infancia-adolescencia_'+this.user.language+'.pdf')
      } else if (parseInt(this.event.type_id) === 17) {
        openSimpleDoc('protocolos/ligaf-protocolo-proteccion-infancia-adolescencia_'+this.user.language+'.pdf')
      }
    },
    openCovid () {
      openCovidDoc(this.user.language)
    },
    openGroupsCategory (category) {
      openCategoryDoc(category)
    },
    openMyTeams (teamId) {
      openMyTeams(teamId, this.event.id, localStorage.token, this.user.language)
    },
    openRevisionPlayers () {
      openStaticExternalDoc('micfootball7/HORA_REVISIO_FITXES_NOALLOTJATS.pdf')
    }
  },
  watch: {
    $route: 'onChangeRoute'
  },
  created () {
    this.onChangeRoute()
  },
  beforeRouteLeave (to, from, next) {
    const scrollY = this.$refs.sidebar ? this.$refs.sidebar.scrollTop : false || 0
    sessionStorage.setItem('left-menu-scroll', scrollY)
    next()
  },
  mounted () {
    this.$nextTick(() => {
      const savedScroll = parseInt(sessionStorage.getItem('left-menu-scroll'), 10)
      if (!isNaN(savedScroll) && this.$refs.sidebar) {
        this.$refs.sidebar.scrollTop = savedScroll
      }
    })
  }
}
</script>

<style lang="scss" scoped src="@/styles/_left_menu.scss">
/* you can leave this empty or add component-specific overrides */
</style>
